import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/welfare';

const hotSlugs = [
  'taobao-fanli-gonglue-2026',
  'meituan-waimai-hongbao-2026',
  'jingdong-plus-huiyuan-2026',
  'xinyongka-quanyi-2026',
  'tmall-618-coupon-2026',
  'apple-education-discount-2026',
];

const latestSlugs = [
  'taobao-fanli-gonglue-2026',
  'jingdong-plus-huiyuan-2026',
  'meituan-waimai-hongbao-2026',
  'xinyongka-quanyi-2026',
  'pinduoduo-lingyuan-2026',
  'huawei-free-trial-2026',
];

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '福利导航在线',
  url: 'https://fulidaohang.online',
  description: '一站式福利资源聚合平台，为您精选全网优质福利资源，涵盖生活优惠、会员权益、折扣活动等多类福利内容。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://fulidaohang.online/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '福利导航在线',
  url: 'https://fulidaohang.online',
  description: '一站式福利资源聚合平台',
  sameAs: [],
};

export default function Home() {
  useSEO({
    title: '福利导航在线 - 一站式福利资源聚合平台',
    description: '福利导航在线为您精选全网优质福利资源，涵盖生活优惠、会员权益、折扣活动等多类福利内容，实时更新，免费使用。无论您寻找购物返利、免费试用还是专属会员福利，福利导航在线都能帮您快速找到最划算的资源入口。',
    keywords: '福利导航在线,在线福利资源导航,免费福利汇总,优惠福利大全,生活福利导航,会员权益导航,折扣优惠导航,福利聚合平台',
    canonicalUrl: 'https://fulidaohang.online',
    schema: { ...websiteSchema, ...orgSchema },
  });

  const hotArticles = hotSlugs.map((slug) => articles.find((a) => a.slug === slug)!).filter(Boolean);
  const latestArticles = latestSlugs.map((slug) => articles.find((a) => a.slug === slug)!).filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20golden%20and%20coral%20abstract%20geometric%20background%20with%20soft%20gradients%20and%20floating%20gift%20icons%2C%20modern%20minimal%20design%2C%20clean%20atmosphere%2C%20elegant%20and%20bright%2C%20subtle%20texture%20patterns%2C%20warm%20inviting%20colors%2C%20digital%20illustration%20style%20with%20soft%20lighting%2C%20Chinese%20welfare%20resource%20navigation%20platform%20hero%20background&width=1600&height=900&seq=hero-welfare-2026&orientation=landscape"
              alt="福利导航在线背景"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
              福利导航在线
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
              一站式福利资源聚合平台，为您精选全网优质福利资源<br className="hidden sm:block" />
              涵盖购物返利、免费试用、会员权益、折扣优惠等丰富内容
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/gouwu-fanli"
                className="px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-200"
              >
                开始探索福利
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-background-50/20 text-background-50 border border-background-50/30 rounded-md font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-background-50/30 transition-all duration-200"
              >
                了解更多
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">200+</div>
                <div className="text-xs text-background-300 mt-1">福利资源</div>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">6</div>
                <div className="text-xs text-background-300 mt-1">福利分类</div>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">50万+</div>
                <div className="text-xs text-background-300 mt-1">月活用户</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                福利分类导航
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                六大福利分类，覆盖生活方方面面，点击进入查看详细福利资源
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-4 md:p-5 bg-background-50 rounded-lg border border-background-200/60 cursor-pointer transition-all duration-300 hover:border-primary-200 hover:bg-primary-50/30"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors font-heading whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8 md:mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  热门福利推荐
                </h2>
                <p className="text-sm text-foreground-600">全网最受欢迎的福利资源精选</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {hotArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8 md:mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  最新福利动态
                </h2>
                <p className="text-sm text-foreground-600">实时更新的最新福利资源</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-accent-500">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
              不想错过任何福利？
            </h2>
            <p className="text-sm md:text-base text-background-200 mb-6">
              关注福利导航在线，每天为您推送最新最全的福利资源，让你的生活处处有惊喜。
            </p>
            <Link
              to="/category/gouwu-fanli"
              className="inline-flex items-center px-6 py-3 bg-background-50 text-accent-600 rounded-md font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-background-100 transition-all duration-200"
            >
              立即开始探索
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}